import { useState, useCallback, useMemo, useEffect } from "react";
import { useQuery as useConvexQuery } from "convex/react";
import { api } from "../../api";

// Simple app data interface
interface AppData {
  id: string;
  name: string;
  iconUrl?: string;
  bundleId?: string;
}

interface CacheEntry {
  data?: AppData;
  loading: boolean;
  error?: string;
}

export const useAppCache = () => {
  const [cache, setCache] = useState<Map<string, CacheEntry>>(new Map());
  const [requestedApps, setRequestedApps] = useState<string[]>([]);

  // Use the apps.get.apps API with specific app IDs
  const appsQuery = useConvexQuery(
    api.apps.get.apps,
    requestedApps.length > 0 ? {
      apps: requestedApps as any[]
    } : "skip"
  );

  // Update cache when new app data is received
  useEffect(() => {
    console.log('appsQuery result:', appsQuery);
    if (appsQuery && Array.isArray(appsQuery)) {
      console.log('Updating cache with apps:', appsQuery.length, 'apps');
      setCache(prevCache => {
        const newCache = new Map(prevCache);
        
        appsQuery.forEach((app: any) => {
          console.log('Caching app:', app._id, 'with data:', app);
          newCache.set(app._id, {
            data: {
              id: app._id,
              name: app.name || 'Unknown App',
              iconUrl: app.iconUrl,
              bundleId: app.bundleId
            },
            loading: false
          });
        });
        
        return newCache;
      });
    }
  }, [appsQuery]);

  const getApp = useCallback((appId: string): AppData => {
    const cached = cache.get(appId);
    
    console.log('getApp called for:', appId, 'cached:', cached);
    
    if (cached?.data) {
      console.log('Returning cached data:', cached.data);
      return cached.data;
    }
    
    // If not cached and not loading, add to request queue
    if (!cached?.loading && !requestedApps.includes(appId)) {
      console.log('Adding to request queue:', appId);
      setRequestedApps(prev => [...prev, appId]);
      setCache(prevCache => {
        const newCache = new Map(prevCache);
        newCache.set(appId, { loading: true });
        return newCache;
      });
    }
    
    // Return placeholder data while loading
    const placeholder = {
      id: appId,
      name: 'Loading...',
      iconUrl: undefined,
      bundleId: appId
    };
    console.log('Returning placeholder:', placeholder);
    return placeholder;
  }, [cache, requestedApps]);

  const getCachedApp = useCallback((appId: string): CacheEntry | undefined => {
    return cache.get(appId);
  }, [cache]);

  const triggerFetch = useCallback((appIds: string[]) => {
    const newAppIds = appIds.filter(id => !requestedApps.includes(id) && !cache.has(id));
    
    console.log('triggerFetch called with:', appIds);
    console.log('newAppIds to fetch:', newAppIds);
    console.log('current requestedApps:', requestedApps);
    console.log('current cache keys:', Array.from(cache.keys()));
    
    if (newAppIds.length > 0) {
      setRequestedApps(prev => [...prev, ...newAppIds]);
      
      setCache(prevCache => {
        const newCache = new Map(prevCache);
        newAppIds.forEach(id => {
          newCache.set(id, { loading: true });
        });
        return newCache;
      });
    }
  }, [requestedApps, cache]);

  const getCacheState = useMemo(() => {
    const entries = Array.from(cache.values());
    const loadingCount = entries.filter(entry => entry.loading).length;
    const loadedCount = entries.filter(entry => entry.data).length;
    
    return {
      loadingCount,
      loadedCount,
      isLoading: loadingCount > 0
    };
  }, [cache]);

  return {
    getApp,
    getCachedApp,
    triggerFetch,
    cache,
    getCacheState
  };
};